export function getPathValue(obj, path) {
  if (!path) return obj;
  return String(path).split(".").reduce((acc, part) => (acc == null ? undefined : acc[part]), obj);
}

export function extractGoogleSheetId(url) {
  if (!url) return "";
  const m = String(url).match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/);
  return m ? m[1] : "";
}

export function extractYouTubeId(input) {
  if (!input) return "";
  const str = String(input).trim();
  if (/^[a-zA-Z0-9_-]{11}$/.test(str)) return str;
  const patterns = [
    /(?:youtube\.com\/watch\?.*v=)([a-zA-Z0-9_-]{11})/,
    /(?:youtu\.be\/)([a-zA-Z0-9_-]{11})/,
    /(?:youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    /(?:youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/,
    /(?:youtube\.com\/v\/)([a-zA-Z0-9_-]{11})/,
  ];
  for (const p of patterns) {
    const m = str.match(p);
    if (m) return m[1];
  }
  return "";
}

export function buildYouTubeSrc(node) {
  const id = extractYouTubeId(node.staticText || "");
  if (!id) return "";
  const p = new URLSearchParams();
  p.set("autoplay",        node.youtubeAutoPlay ? "1" : "0");
  p.set("mute",            node.youtubeMute ? "1" : "0");
  p.set("controls",        node.youtubeControls !== false ? "1" : "0");
  p.set("loop",            node.youtubeLoop ? "1" : "0");
  if (node.youtubeLoop) p.set("playlist", id);
  if (node.youtubeStart > 0) p.set("start", String(Math.floor(node.youtubeStart)));
  if (node.youtubeEnd   > 0) p.set("end",   String(Math.floor(node.youtubeEnd)));
  // Always strip all overlay UI
  p.set("rel",             "0");   // no related videos
  p.set("modestbranding",  "1");   // minimal branding
  p.set("showinfo",        "0");   // hide title bar (legacy param, still helps)
  p.set("iv_load_policy",  "3");   // no annotations
  p.set("cc_load_policy",  "0");   // no captions by default
  p.set("fs",              "0");   // no fullscreen button
  p.set("disablekb",       "1");   // no keyboard shortcuts
  p.set("playsinline",     "1");   // inline on iOS
  p.set("enablejsapi",     "0");
  return `https://www.youtube.com/embed/${id}?${p.toString()}`;
}

export async function fetchGoogleSheetRows(sheetUrl, sheetGid = "0") {
  const sheetId = extractGoogleSheetId(sheetUrl);
  if (!sheetId) throw new Error("Invalid Google Sheet URL");
  const gid = sheetGid || "0";
  const cacheBust = Date.now();
  const url = `https://docs.google.com/spreadsheets/d/${sheetId}/gviz/tq?tqx=out:json&gid=${encodeURIComponent(gid)}&cb=${cacheBust}`;
  const res = await fetch(url, { cache: "no-store" });
  const text = await res.text();
  const start = text.indexOf("{");
  const end = text.lastIndexOf("}");
  if (start < 0 || end < 0) throw new Error("Unexpected Google Sheets response");
  const json = JSON.parse(text.slice(start, end + 1));
  const cols = (json.table?.cols || []).map((c, i) => c.label || c.id || `col${i + 1}`);
  const rows = json.table?.rows || [];
  return rows.map((r) => {
    const out = {};
    (r.c || []).forEach((cell, idx) => {
      out[cols[idx]] = cell ? cell.v : null;
    });
    return out;
  });
}

export async function testDataSourceFetch(source) {
  if (!source) return null;
  const type = source.type || "api";

  if (type === "rss") {
    const { fetchRSSFeed } = await import("../../../utils/rssParser");
    const items = await fetchRSSFeed(source.rssFeedUrl || "");
    return items.slice(0, source.maxItems || 20);
  }

  if (type === "googleSheets") {
    return await fetchGoogleSheetRows(source.sheetUrl || "", source.sheetGid || "0");
  }

  const url = source.url || "";
  if (!url) throw new Error("URL is required");
  let headers = { "Content-Type": "application/json" };
  if (source.headersJSON) {
    headers = { ...headers, ...JSON.parse(source.headersJSON) };
  }
  (source.headers || []).forEach(h => { if (h.key) headers[h.key] = h.literalValue || ""; });
  const method = source.method || "GET";
  const params = source.params || [];
  const queryRows = params.filter(p => p.type === "query" && p.key);
  const bodyRows = params.filter(p => p.type === "body" && p.key);
  let finalUrl = url;
  if (queryRows.length) {
    const qs = new URLSearchParams();
    queryRows.forEach(p => qs.append(p.key, p.literalValue || ""));
    finalUrl += (finalUrl.includes("?") ? "&" : "?") + qs.toString();
  }
  let body = undefined;
  if (method !== "GET") {
    if (bodyRows.length) {
      const obj = {};
      bodyRows.forEach(p => { obj[p.key] = p.literalValue || ""; });
      body = JSON.stringify(obj);
    } else if (source.bodyJSON) {
      body = source.bodyJSON;
    }
  }
  const res = await fetch(finalUrl, { method, headers, body });
  let payload;
  try { payload = await res.json(); } catch (_e) { payload = await res.text(); }
  return source.responseKey ? getPathValue(payload, source.responseKey) : payload;
}
